import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { ColorField } from "@/components/cms/theme/ColorField";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";
import { Label } from "@/components/ui/label";
import {
  type BlockBorder,
  type BorderLineStyle,
  type SpacingSide,
} from "@/lib/cms-puck/block-style";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { cn } from "@/lib/utils";
import { StyleSection } from "./StyleSection";
import { ControlRow, IconToggle } from "./controls";

/* ------------------------------- Bordures --------------------------------- */

const BORDER_STYLE_LABELS: Array<
  [BorderLineStyle, CmsNewKey, string]
> = [
  ["solid", "cms.new.auto.option.solid.continu", "Continu"],
  ["dashed", "cms.new.auto.option.dashed.tirets", "Tirets"],
  ["dotted", "cms.new.auto.option.dotted.pointilles", "Pointillés"],
];

const BORDER_SIDE_LABELS: Array<[keyof BlockBorder, CmsNewKey, string]> = [
  ["top", "cms.new.auto.option.top.haut", "Haut"],
  ["right", "cms.new.auto.option.right.droite", "Droite"],
  ["bottom", "cms.new.auto.option.bottom.bas", "Bas"],
  ["left", "cms.new.auto.option.left.gauche", "Gauche"],
];

function uniformBorderWidth(border: BlockBorder): number | null {
  const w = border.top.width;
  if (
    w != null &&
    w === border.right.width &&
    w === border.bottom.width &&
    w === border.left.width
  ) {
    return w;
  }
  return null;
}

function borderSideWidthValue(width: SpacingSide): string | number {
  return width != null ? width : "";
}

function BorderSideInput({
  side,
  label,
  width,
  onChange,
}: {
  side: keyof BlockBorder;
  label: string;
  width: SpacingSide;
  onChange: (side: keyof BlockBorder, width: SpacingSide) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <input
        type="number"
        min={0}
        value={borderSideWidthValue(width)}
        placeholder={cmsNew("cms.new.auto.placeholder.0", "0")}
        aria-label={`${cmsNew("cms.new.fields.style.borderAria", "Bordure")} ${label}`}
        className={cn(
          CMS_PALETTE_TEXT_INPUT_CLASS,
          "h-9 w-full text-center tabular-nums",
        )}
        onChange={(e) => {
          const raw = e.target.value;
          const n = raw === "" ? null : Number(raw);
          onChange(
            side,
            n != null && Number.isFinite(n) && n >= 0 ? n : null,
          );
        }}
      />
    </div>
  );
}

/**
 * Bordures génériques du bloc : largeur par côté, style + couleur partagés
 * entre les côtés (même modèle d'UI que ButtonAppearanceField).
 */
export function BorderControls({
  border,
  colors,
  onBorderChange,
}: {
  border: BlockBorder;
  colors: CmsColor[];
  onBorderChange: (border: BlockBorder) => void;
}) {
  const cmsNew = useCmsNew();
  const setSideWidth = (side: keyof BlockBorder, width: SpacingSide) =>
    onBorderChange({ ...border, [side]: { ...border[side], width } });
  // Style + couleur communs : lus sur le côté haut, écrits sur les 4 côtés.
  const setShared = (patch: { style?: BorderLineStyle; color?: BlockBorder["top"]["color"] }) =>
    onBorderChange({
      top: { ...border.top, ...patch },
      right: { ...border.right, ...patch },
      bottom: { ...border.bottom, ...patch },
      left: { ...border.left, ...patch },
    });

  const setUniformWidth = (width: number | null) => {
    const side = { ...border.top, width };
    onBorderChange({ top: side, right: side, bottom: side, left: side });
  };

  const BORDER_PRESETS: Array<[number, string]> = [
    [0, "0"],
    [1, "1"],
    [2, "2"],
    [4, "4"],
  ];

  return (
    <StyleSection
      title={cmsNew("cms.new.auto.title.bordures", "Bordures")}
      description={cmsNew("cms.new.auto.description.largeur_par_cote_px_style_et_couleur_communs", "Largeur par côté (px), style et couleur communs.")}
    >
      <div className="space-y-4">
        <ControlRow label={cmsNew("cms.new.auto.label.epaisseur_rapide", "Épaisseur rapide")}>
          {BORDER_PRESETS.map(([width, short]) => (
            <IconToggle
              key={short}
              label={`${short} px`}
              active={
                border.top.width === width &&
                border.right.width === width &&
                border.bottom.width === width &&
                border.left.width === width
              }
              onClick={() => setUniformWidth(width)}
            >
              <span className="text-[10px] font-semibold tabular-nums">
                {short}
              </span>
            </IconToggle>
          ))}
          <input
            type="number"
            min={0}
            value={uniformBorderWidth(border) ?? ""}
            placeholder={cmsNew("cms.new.auto.placeholder.px", "px")}
            title={cmsNew("cms.new.auto.title.epaisseur_uniforme_personnalisee_px", "Épaisseur uniforme personnalisée (px)")}
            className={cn(
              CMS_PALETTE_TEXT_INPUT_CLASS,
              "h-9 w-20 text-center tabular-nums",
            )}
            onChange={(e) => {
              const raw = e.target.value;
              const n = raw === "" ? null : Number(raw);
              setUniformWidth(
                n != null && Number.isFinite(n) && n >= 0 ? n : null,
              );
            }}
          />
          <span className="text-[10px] text-slate-400">{cmsNew("cms.new.auto.text.px", "px")}</span>
        </ControlRow>

        {/* 2 colonnes : inputs assez larges pour lire la valeur (cf. radius). */}
        <div className="grid grid-cols-2 gap-3">
          {BORDER_SIDE_LABELS.map(([side, key, fallback]) => (
            <BorderSideInput
              key={side}
              side={side}
              label={cmsNew(key, fallback)}
              width={border[side].width}
              onChange={setSideWidth}
            />
          ))}
        </div>

        <ControlRow label={cmsNew("cms.new.auto.label.style", "Style")}>
          <Select
            value={border.top.style}
            onValueChange={(v) => setShared({ style: v as BorderLineStyle })}
          >
            <SelectTrigger className="h-9 w-36 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BORDER_STYLE_LABELS.map(([value, key, fallback]) => (
                <SelectItem key={value} value={value}>
                  {cmsNew(key, fallback)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ControlRow>

        <ColorField
          label={cmsNew("cms.new.auto.label.couleur", "Couleur")}
          colors={colors}
          value={border.top.color}
          onChange={(color) => setShared({ color })}
        />
      </div>
    </StyleSection>
  );
}
